import React, { useState, useRef, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Divider,
} from "@mui/material";
import { X, Paperclip, ArrowUpRight } from "lucide-react";
import { Sparkles } from "@mynaui/icons-react";
import { useAuth } from "../../shared/context/AuthContext";
import { ROLES } from "../../shared/config/roleConfig";

const MOCK_DATA = {
  [ROLES.PLANT_DIRECTOR]: {
    initialMessages: [
      {
        role: "user",
        text: "What is the most critical issue I need to address right now?",
      },
      {
        role: "bot",
        thinkTime: 2,
        text: "The highest priority is Pump-A12, which has been flagged with a High Bearing Failure Risk. It has failed 3 times in the last year, and our model suggests scheduling a bearing replacement within the next 2 weeks to avoid unplanned downtime.",
      },
    ],
    qa: [
      {
        keywords: ["cost", "impact", "downtime", "quarter"],
        answer:
          "Unplanned downtime has cost approximately $284K this quarter across all zones. The Finishing Zone accounts for 45% of this cost, primarily due to Pump-A12 and Compressor-C3 failures. Proactive maintenance could reduce this by an estimated 35%.",
      },
      {
        keywords: ["prioritize", "maintenance", "next week", "assets"],
        answer:
          "Based on current risk scores and failure predictions, I recommend prioritizing:\n\n1) Pump-A12 (Finishing Zone) — Bearing failure risk at 87%\n2) Compressor-C3 (Assembly Zone) — Vibration anomaly detected, risk at 72%\n3) Conveyor-B7 (Packaging Zone) — Belt wear detected, risk at 65%\n\nScheduling these three would reduce your overall plant risk score by 18%.",
      },
    ],
    suggestions: [
      {
        question: "What's the downtime cost this quarter?",
        answer:
          "Unplanned downtime has cost approximately $284K this quarter across all zones. The Finishing Zone accounts for 45% of this cost, primarily due to Pump-A12 and Compressor-C3 failures. Proactive maintenance could reduce this by an estimated 35%.",
      },
      {
        question: "Which assets should I prioritize for maintenance?",
        answer:
          "Based on current risk scores and failure predictions, I recommend prioritizing:\n\n1) Pump-A12 (Finishing Zone) — Bearing failure risk at 87%\n2) Compressor-C3 (Assembly Zone) — Vibration anomaly detected, risk at 72%\n3) Conveyor-B7 (Packaging Zone) — Belt wear detected, risk at 65%\n\nScheduling these three would reduce your overall plant risk score by 18%.",
      },
      {
        question: "How is the Finishing Zone performing?",
        answer:
          "The Finishing Zone currently has the highest risk profile in the plant. Key concerns:\n\n• Pump-A12: Bearing failure risk at 87%, 3 failures in the past year\n• Overall zone uptime: 91.2% (below the 95% target)\n• 2 open work orders pending completion\n\nI recommend scheduling preventive maintenance for Pump-A12 this week to improve zone reliability.",
      },
    ],
  },
  [ROLES.TECHNICIAN]: {
    initialMessages: [
      {
        role: "user",
        text: "What tasks are assigned to me today?",
      },
      {
        role: "bot",
        thinkTime: 2,
        text: "You have 2 tasks assigned for today:\n\n1) Bearing inspection on Pump-A12 in the Finishing Zone — Priority: High, due by 2:00 PM.\n2) Vibration sensor calibration on Compressor-C3 in the Assembly Zone — Priority: Medium, due by 5:00 PM.\n\nWould you like detailed steps for either task?",
      },
    ],
    qa: [
      {
        keywords: ["tools", "need", "pump", "bearing", "inspection"],
        answer:
          "For the Pump-A12 bearing inspection, you'll need:\n\n1) Vibration analyzer (SKF CMXA 80)\n2) Infrared thermometer\n3) Bearing puller set (40–75mm)\n4) Replacement bearing SKF 6310-2RS if needed\n\nThe bearing was last replaced 8 months ago and has logged 4,200 operating hours. Check the inner race for pitting — that was the failure mode in the last 2 incidents.",
      },
      {
        keywords: ["maintenance", "history", "compressor", "C3"],
        answer:
          "Compressor-C3 maintenance history (last 6 months):\n\n• Jan 15: Oil change and filter replacement (Scheduled)\n• Feb 28: Vibration anomaly detected, belt tension adjusted (Corrective)\n• Mar 20: Quarterly inspection completed, all within spec (Scheduled)\n• May 5: Current vibration anomaly detected by sensor VS-103. Amplitude increased 22% from baseline.\n\nRecommended action: Check belt alignment and compressor mount bolts.",
      },
    ],
    suggestions: [
      {
        question: "What tools do I need for Pump-A12 inspection?",
        answer:
          "For the Pump-A12 bearing inspection, you'll need:\n\n1) Vibration analyzer (SKF CMXA 80)\n2) Infrared thermometer\n3) Bearing puller set (40–75mm)\n4) Replacement bearing SKF 6310-2RS if needed\n\nThe bearing was last replaced 8 months ago and has logged 4,200 operating hours. Check the inner race for pitting — that was the failure mode in the last 2 incidents.",
      },
      {
        question: "Show Compressor-C3 maintenance history",
        answer:
          "Compressor-C3 maintenance history (last 6 months):\n\n• Jan 15: Oil change and filter replacement (Scheduled)\n• Feb 28: Vibration anomaly detected, belt tension adjusted (Corrective)\n• Mar 20: Quarterly inspection completed, all within spec (Scheduled)\n• May 5: Current vibration anomaly detected by sensor VS-103. Amplitude increased 22% from baseline.\n\nRecommended action: Check belt alignment and compressor mount bolts.",
      },
      {
        question: "What's the priority of my tasks today?",
        answer:
          "Here's your task priority breakdown:\n\n🔴 High Priority:\n• Bearing inspection on Pump-A12 (Finishing Zone) — Due by 2:00 PM. This asset has an 87% failure risk and has been escalated.\n\n🟡 Medium Priority:\n• Vibration sensor calibration on Compressor-C3 (Assembly Zone) — Due by 5:00 PM. Anomaly detected but stable for now.\n\nI recommend completing the Pump-A12 inspection first.",
      },
    ],
  },
};

const GENERIC_RESPONSE =
  "I'm analyzing the data for your request. Based on the current sensor readings and maintenance records, I'd recommend reviewing the asset detail page for the most up-to-date information. Is there a specific asset or metric you'd like me to focus on?";

function findAnswer(role, input) {
  const data = MOCK_DATA[role] || MOCK_DATA[ROLES.PLANT_DIRECTOR];
  const lower = input.toLowerCase();
  for (const entry of data.qa) {
    const matched = entry.keywords.filter((kw) => lower.includes(kw)).length;
    if (matched >= 2) return entry.answer;
  }
  return GENERIC_RESPONSE;
}

function ChatBotModal({ open, onClose }) {
  const { user } = useAuth();
  const role = user?.role || ROLES.PLANT_DIRECTOR;
  const data = MOCK_DATA[role] || MOCK_DATA[ROLES.PLANT_DIRECTOR];

  const [messages, setMessages] = useState(data.initialMessages);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [usedSuggestions, setUsedSuggestions] = useState(new Set());
  const messagesEndRef = useRef(null);
  const prevRoleRef = useRef(role);

  // Reset messages when role changes
  useEffect(() => {
    if (prevRoleRef.current !== role) {
      const newData = MOCK_DATA[role] || MOCK_DATA[ROLES.PLANT_DIRECTOR];
      setMessages(newData.initialMessages);
      prevRoleRef.current = role;
    }
  }, [role]);

  // Reset messages when modal opens
  useEffect(() => {
    if (open) {
      const newData = MOCK_DATA[role] || MOCK_DATA[ROLES.PLANT_DIRECTOR];
      setMessages(newData.initialMessages);
      setInput("");
      setThinking(false);
      setUsedSuggestions(new Set());
    }
  }, [open, role]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, thinking]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || thinking) return;

    const userMsg = { role: "user", text: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setThinking(true);

    const thinkTime = Math.floor(Math.random() * 2) + 2; // 2-3 seconds
    setTimeout(() => {
      const answer = findAnswer(role, trimmed);
      setMessages((prev) => [
        ...prev,
        { role: "bot", thinkTime, text: answer },
      ]);
      setThinking(false);
    }, thinkTime * 1000);
  };

  const handleSuggestionClick = (suggestion) => {
    if (thinking) return;
    setUsedSuggestions((prev) => new Set(prev).add(suggestion.question));
    const userMsg = { role: "user", text: suggestion.question };
    setMessages((prev) => [...prev, userMsg]);
    setThinking(true);

    const thinkTime = Math.floor(Math.random() * 2) + 2;
    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        { role: "bot", thinkTime, text: suggestion.answer },
      ]);
      setThinking(false);
    }, thinkTime * 1000);
  };

  const availableSuggestions = data.suggestions?.filter(
    (s) => !usedSuggestions.has(s.question)
  ) || [];

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: 480,
            maxWidth: "100vw",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow: "-4px 0px 24px rgba(0, 0, 0, 0.1)",
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "18px", color: "#020618" }}>
          Chat with Agent
        </Typography>
        <IconButton onClick={onClose} size="small">
          <X size={20} color="#6B7280" />
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: "#E2E8F0" }} />

      {/* Messages */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          px: 3,
          py: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {messages.map((msg, idx) =>
          msg.role === "user" ? (
            <Box
              key={idx}
              sx={{
                alignSelf: "flex-end",
                maxWidth: "80%",
              }}
            >
              <Box
                sx={{
                  bgcolor: "#F3F4F6",
                  borderRadius: "12px",
                  px: 2,
                  py: 1.5,
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "#020618", lineHeight: 1.5 }}>
                  {msg.text}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box key={idx} sx={{ alignSelf: "flex-start", maxWidth: "85%" }}>
              {msg.thinkTime && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    mb: 1,
                  }}
                >
                  <Sparkles size={14} color="#6B7280" />
                  <Typography sx={{ fontSize: "12px", color: "#6B7280", fontWeight: 500 }}>
                    Thought for {msg.thinkTime} seconds
                  </Typography>
                </Box>
              )}
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#020618",
                  lineHeight: 1.6,
                  whiteSpace: "pre-line",
                }}
              >
                {msg.text}
              </Typography>
            </Box>
          )
        )}

        {/* Thinking indicator */}
        {thinking && (
          <Box sx={{ alignSelf: "flex-start" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <Sparkles size={14} color="#6B7280" />
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#6B7280",
                  fontWeight: 500,
                  "@keyframes pulse": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.4 },
                  },
                  animation: "pulse 1.5s ease-in-out infinite",
                }}
              >
                Thinking...
              </Typography>
            </Box>
          </Box>
        )}

        {/* Suggestion chips */}
        {!thinking && availableSuggestions.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              mt: 1,
            }}
          >
            {availableSuggestions.map((s, idx) => (
              <Box
                key={idx}
                onClick={() => handleSuggestionClick(s)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  border: "1px solid #E2E8F0",
                  borderRadius: "10px",
                  px: 1.5,
                  py: 1,
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "#399432",
                    bgcolor: "#F0FDF4",
                  },
                }}
              >
                <ArrowUpRight size={14} color="#399432" style={{ flexShrink: 0 }} />
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: "#374151",
                    fontWeight: 500,
                    lineHeight: 1.4,
                  }}
                >
                  {s.question}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input area */}
      <Box
        sx={{
          px: 2.5,
          pb: 2.5,
          pt: 1.5,
        }}
      >
        <Box
          sx={{
            border: "1px solid #E2E8F0",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={3}
            placeholder="Ask a question ..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            variant="standard"
            InputProps={{
              disableUnderline: true,
            }}
            sx={{
              px: 2,
              pt: 1.5,
              pb: 0.5,
              "& .MuiInputBase-input": {
                fontSize: "14px",
                color: "#020618",
                "&::placeholder": {
                  color: "#9CA3AF",
                  opacity: 1,
                },
              },
            }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1.5,
              pb: 1.5,
            }}
          >
            <IconButton size="small" sx={{ color: "#9CA3AF" }}>
              <Paperclip size={18} />
            </IconButton>
            <Box
              onClick={handleSend}
              sx={{
                bgcolor: "#399432",
                color: "#fff",
                borderRadius: "8px",
                px: 2,
                py: 0.75,
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                "&:hover": { bgcolor: "#2E7628" },
                opacity: input.trim() && !thinking ? 1 : 0.6,
              }}
            >
              Send
            </Box>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}

export default ChatBotModal;
